import { FC } from 'react';
import { Routes, Route, useResolvedPath } from 'react-router-dom';
import { UserOutlined, TeamOutlined, FileSearchOutlined, LockOutlined, IdcardOutlined, ToolOutlined, DollarOutlined, SettingOutlined } from '@ant-design/icons';
import { SectionLayoutContainer } from './section-layout.container';
import { Cases } from '../cases/routes';
import { Entity } from './pages/entity';
import { Role } from './pages/role';
import { Staff as StaffPage } from './pages/staff';
import { FinanceConfig } from './pages/finance-config';
import { Applicant } from './pages/applicant';
import MaintenanceMessage from '../../shared/maintenance-message/maintenance-message';
import { useMaintenanceMessage } from '../../shared/maintenance-message';
import { Skeleton } from 'antd';
import { IsConfigPage } from './pages/is-config';
import { StaffRole } from '../../../generated';
import { Tools } from './pages/tools';
import { LandingPageContainer } from '../../shared/landing-page.container';

export interface PageLayoutProps {
  path: string;
  title: string;
  icon: React.JSX.Element;
  id: string | number;
  parent?: string;
  hasPermissions?: (role: StaffRole) => boolean;
}

export const Staff: FC = () => {
  const casesRoutePath = useResolvedPath('cases');
  const applicantRoutePath = useResolvedPath('applicants');
  const entitiesRoutePath = useResolvedPath('entities');
  const rolesRoutePath = useResolvedPath('roles');
  const isConfigRoutePath = useResolvedPath('is-config');
  const financeConfigRoutePath = useResolvedPath('finance-config');
  const staffRoutePath = useResolvedPath('staff');
  const toolsRoutePath = useResolvedPath('tools');

  const pageLayouts: PageLayoutProps[] = [
    {
      path: `${casesRoutePath.pathname}`,
      title: 'Cases',
      icon: <FileSearchOutlined />,
      id: '1',
      hasPermissions: (role) => {
        return (
          role?.permissions?.idCasePermissions?.canSearchCases ||
          role?.permissions?.credCasePermissions?.canSearchCases ||
          role?.permissions?.sendCasePermissions?.canSearchCases
        );
      }
    },
    {
      path: `${applicantRoutePath.pathname}`,
      title: 'Applicants',
      icon: <UserOutlined />,
      id: '2',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.permissions?.applicantUserPermissions?.canSearchApplicants;
      }
    },
    {
      path: `${entitiesRoutePath.pathname}`,
      title: 'Entities',
      icon: <TeamOutlined />,
      id: '3',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.permissions?.entityPermissions?.canSearchEntities;
      }
    },
    {
      path: `${rolesRoutePath.pathname}`,
      title: 'Roles',
      icon: <LockOutlined />,
      id: '4',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.permissions?.rolePermissions?.canSearchRoles;
      }
    },
    {
      path: `${isConfigRoutePath.pathname}`,
      title: 'IS Config',
      icon: <SettingOutlined />,
      id: '5',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.roleSubtype === 'Staff.IS';
      }
    },
    {
      path: `${financeConfigRoutePath.pathname}`,
      title: 'Finance',
      icon: <DollarOutlined />,
      id: '6',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.roleSubtype === 'Staff.Finance' || role?.roleSubtype === 'Staff.IS';
      }
    },
    {
      path: `${staffRoutePath.pathname}`,
      title: 'Staff',
      icon: <IdcardOutlined />,
      id: '7',
      parent: 'ROOT',
      hasPermissions: (role) => {
        return role?.permissions?.staffUserPermissions?.canSearchStaff;
      }
    },
    {
      path: `${toolsRoutePath.pathname}`,
      title: 'Tools',
      icon: <ToolOutlined />,
      id: '8',
      parent: 'ROOT'
    }
  ];
  const { isImpending, isMaintenance } = useMaintenanceMessage();

  const routes = (
    <Routes>
      <Route path="" element={<SectionLayoutContainer pageLayouts={pageLayouts} />}>
        <Route path={'cases/*'} element={<Cases />} />
        <Route path={'applicants/*'} element={<Applicant />} />
        <Route path={'entities/*'} element={<Entity />} />
        <Route path={'roles/*'} element={<Role />} />
        <Route path={'is-config/*'} element={<IsConfigPage />} />
        <Route path={'finance-config/*'} element={<FinanceConfig />} />
        <Route path={'staff/*'} element={<StaffPage />} />
        <Route path={'tools/*'} element={<Tools />} />
        <Route path={'landing-page/*'} element={<LandingPageContainer />} />
      </Route>
    </Routes>
  );

  if (isMaintenance === undefined) {
    return <Skeleton />;
  }
  if (isMaintenance) {
    return <MaintenanceMessage />;
  }
  if (isImpending === undefined) return <Skeleton />;

  if (isImpending) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {/* <ImpendingMessage /> */}
        {routes}
      </div>
    );
  }

  return routes;
};
